import { useCallback, useEffect, useState } from 'react';
import core, { API, App, GQL } from '@gin-betting/core';

import { BET_SETTLED_STATUSES, BET_UNSETTLED_STATUSES } from 'app-constants';
import features from 'services/feature-manager';
import GET_BETS from './gqlGetBets';

interface GetBetsQuery {
    bets: Array<{
        stake: string;
    }>;
}

const {
    appServiceLocator,
    utils: { getBettingOptions },
} = core;
const LAST_BET_TIMEOUT = 1.5 * 1000;

interface Output {
    isLoaded: boolean;
    lastBetSum: number | undefined;
}

const useLastBetSum = (): Output => {
    const [output, setOutput] = useState<Output>({
        lastBetSum: undefined,
        isLoaded: false,
    });

    const getLastBet = useCallback(() => {
        const apiClient = appServiceLocator.get('apiClient');
        const variables: GQL.GetBetsQueryVariables = {
            input: {
                limit: 1,
                statuses: [...BET_UNSETTLED_STATUSES, ...BET_SETTLED_STATUSES],
                offset: 0,
            },
        };

        Promise.race([
            apiClient.makeRequestWithCustomQuery<
                GetBetsQuery,
                GQL.GetBetsQueryVariables,
                API.Action
            >({
                variables,
                query: GET_BETS as any,
                action: {
                    type: 'hooks/GET_BETS',
                    payload: {},
                },
            }),
            new Promise<
                API.SuccessResponse<App.TypedFetchResult<GetBetsQuery>>
            >(resolve =>
                setTimeout(resolve, LAST_BET_TIMEOUT, {
                    data: { bets: [] } as GQL.GetBetsQuery,
                })
            ),
        ])
            .then(
                ({
                    response: {
                        data: { bets },
                    },
                }) => {
                    const [lastBet] = bets;

                    setOutput({
                        isLoaded: true,
                        lastBetSum: lastBet ? +lastBet.stake : undefined,
                    });
                }
            )
            .catch(error => {
                console.error(error);
                setOutput({
                    isLoaded: true,
                    lastBetSum: undefined,
                });
            });
    }, []);

    useEffect(() => {
        const isSetLastBet = features.isActive(
            features.list.BetslipRememberLastBetSum
        );

        if (!isSetLastBet || !getBettingOptions().isAuthorized) {
            setOutput({ isLoaded: true, lastBetSum: undefined });

            return;
        }

        getLastBet();
    }, [getLastBet]);

    return output;
};

export default useLastBetSum;
